import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { flow, types, getRoot } from "mobx-state-tree";
import { OrgRole, type IOrgRole } from "./models";
import type { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { queryClient } from "@/lib/queryClient";
import { identifyUser, resetUser } from "@/lib/posthog/client";
import { track } from "@/lib/posthog/events";
import { teardownAllResyncChannels } from "@/lib/realtime/channel";
import { clearPersistedIssueFilters } from "@/domains/pm/utils/issueFilterStorage";
import type { AuthRootAccess } from "./rootAccess";
import type { Permission } from "@/constants/permissions";
import * as permRepo from "./permissions/repository";
import type { ProjectAssignment } from "./permissions/repository";
import type { ConfigurablePermission } from "@/lib/memberProjectPermissions";

/** Delay before the FIRST re-attempt of a failed RBAC load; doubles from there. */
const RBAC_RETRY_DELAY_MS = 15_000;

/** Ceiling for the doubling, so a long outage still gets re-attempted at a sane rate. */
const RBAC_RETRY_MAX_DELAY_MS = 4 * 60_000;

/** Re-attempts before giving up: 15s + 30s + 1m + 2m + 4m ≈ 8 minutes of trying. */
const RBAC_RETRY_MAX_ATTEMPTS = 5;

/**
 * Wall-clock the whole retry ladder spans, i.e. how long a failed RBAC load can
 * still recover by itself.
 *
 * Exported because anything WAITING for that recovery needs the same deadline:
 * once the attempts are spent nothing re-fires either load for the org the user
 * is already on, so `isRbacLoadedFor` can never flip on its own and an
 * unbounded wait would last the session (see the org switch's late-RBAC
 * watcher). Summed from the ladder rather than written out, so the two cannot
 * drift apart.
 */
export const RBAC_RETRY_BUDGET_MS = Array.from(
  { length: RBAC_RETRY_MAX_ATTEMPTS },
  (_unused, index) => Math.min(RBAC_RETRY_DELAY_MS * 2 ** index, RBAC_RETRY_MAX_DELAY_MS)
).reduce((total, delay) => total + delay, 0);

/**
 * Delay before the next re-attempt of a failed RBAC load, or `null` once the
 * attempts for `retryKey` are spent.
 *
 * Backed off AND capped in count, because not every failure is transient: an
 * ambiguous-embed or missing-function error (PGRST201 and friends) is a schema
 * fact that answering the same question again cannot change, and a fixed 15s
 * timer would re-issue that request pair every 15s for the rest of the session.
 * Rather than guess which error codes are permanent — the repository flattens
 * PostgrestError to Error, and a schema-cache miss does recover after a reload —
 * the retry gives every failure the same bounded effort and then stops.
 */
function nextRbacRetryDelay(attempts: Map<string, number>, retryKey: string): number | null {
  const attempt = (attempts.get(retryKey) ?? 0) + 1;
  if (attempt > RBAC_RETRY_MAX_ATTEMPTS) return null;
  attempts.set(retryKey, attempt);
  return Math.min(RBAC_RETRY_DELAY_MS * 2 ** (attempt - 1), RBAC_RETRY_MAX_DELAY_MS);
}

/** The house idiom, once, because both RBAC loads record a failure the same way. */
function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * Re-attempt a failed RBAC load, or report that its budget is spent.
 *
 * Nothing else re-fires either load for the org the user is already on
 * (OrganizationContext fires them only on set/switch), so without this a failed
 * first load is a silent permanent deny until a page reload.
 */
function scheduleRbacRetry(
  store: {
    rbacRetryTimers: Map<string, ReturnType<typeof setTimeout>>;
    rbacRetryAttempts: Map<string, number>;
  },
  load: { retryKey: string; what: string; orgId: string; error: unknown },
  retry: () => void
): void {
  const { retryKey } = load;
  // Both maps are taken once, by reference: every line below works on the store's
  // own maps, and the timer callback closes over the same handle table the
  // cancelling code reads.
  const { rbacRetryTimers: timers, rbacRetryAttempts: attempts } = store;
  clearTimeout(timers.get(retryKey));
  const delay = nextRbacRetryDelay(attempts, retryKey);
  if (delay === null) {
    timers.delete(retryKey);
    // Every interpolated value is an ARGUMENT, not part of the format string —
    // console's first argument IS the format, so building it by concatenation
    // is how a `%s` arriving in the data swallows the values after it.
    console.error(
      "[rbac] load failed; giving up until the next sign-in or org switch. what:",
      load.what,
      "attempts:",
      RBAC_RETRY_MAX_ATTEMPTS,
      "org:",
      load.orgId,
      load.error
    );
    return;
  }
  // Named rather than written inline, so the scheduling line below reads as the
  // one statement it is.
  const onRetryDue = (): void => {
    // The timer has fired; its handle is dead either way. Drop the entry before
    // anything else so the map cannot accumulate stale handles for orgs the user
    // has left — clearTimeout on one of those is a no-op that would silently fail
    // to cancel the live retry it was believed to hold.
    timers.delete(retryKey);
    retry();
  };
  timers.set(retryKey, setTimeout(onRetryDue, delay));
}

/**
 * Reported when a session exists but its `user_profiles` row does not — see loadSession.
 * Exported because it is the one hydration failure re-reading cannot resolve: TosConsentGate
 * tells a missing row apart from a transient read failure by it.
 */
export const NO_PROFILE_ROW_ERROR = "No user profile found for the signed-in account";

/**
 * Reported when a sign-out landed while loadSession was mid-flight — see `authEpoch`.
 * Exported because it is the one hydration failure a caller must NOT retry: re-running
 * loadSession would hydrate the session the sign-out just dropped (AuthCallback).
 */
export const SIGNED_OUT_MID_LOAD = "Signed out while the session was loading";

/**
 * How long a sign-out may keep this browser's refresh token in storage while it waits for the
 * server-side revoke — see signOutWithinBudget.
 */
const SIGN_OUT_TIMEOUT_MS = 5_000;

const SIGN_OUT_TIMED_OUT = "Sign-out did not complete in time";

/** Pause before the one re-read of a missing profile row — see fetchProfileRow. */
const PROFILE_RETRY_DELAY_MS = 500;

/**
 * Read the signed-in user's `user_profiles` row, re-reading once when it comes back empty.
 *
 * The row is created by the `handle_new_user` trigger on the same transaction that inserts
 * `auth.users`, so a brand-new OAuth user can reach this before their just-issued JWT can
 * read it back. That read-your-write race is otherwise terminal: loadSession reports the
 * miss, AuthCallback treats any failure as fatal and force-signs the user out of their
 * first-ever sign-in, with nothing to re-try it. An error is returned as-is — RLS denial or
 * a transport failure is not a race, and retrying it only delays the report.
 *
 * ponytail: one retry at a fixed delay. Give it a real backoff only if the trigger is shown
 * to land later than this; the caller's own budget (CALLBACK_TIMEOUT_MS) is 15s.
 */
async function fetchProfileRow(authUserId: string) {
  const read = () =>
    supabase
      .from("user_profiles")
      .select("id, is_platform_admin")
      .eq("auth_user_id", authUserId)
      .maybeSingle();

  const first = await read();
  if (first.error || first.data) return first;

  await new Promise((resolve) => setTimeout(resolve, PROFILE_RETRY_DELAY_MS));
  return read();
}

/**
 * Drop every supabase auth item from localStorage — `sb-<project-ref>-auth-token` and the
 * `sb-localhost-auth-token` a local stack writes. Collected first because removing during
 * the scan reindexes `localStorage.key(i)` and would skip entries.
 *
 * Never throws. It runs in logout's `finally`, and logout must never reject: AuthCallback's
 * failAndSignOut awaits it with no catch, so a throw here would skip the failure screen and
 * leave the user on the spinner until the callback timeout. With site storage blocked
 * (Safari/Brave) the FIRST property access throws `SecurityError` — which is also the case
 * where nothing was persisted to clear, since `client.ts` hands the same unavailable
 * localStorage to createClient only when it is usable (`isLocalStorageAvailable`).
 */
function clearSupabaseAuthStorage(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes("-auth-token") || key.startsWith("sb-"))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage unreadable — see above: there is nothing persisted to remove.
  }
}

/**
 * `supabase.auth.signOut()`, bounded, so that what has to happen after it always happens.
 *
 * signOut awaits auth-js's `initializePromise` and then `_acquireLock(-1, …)` — the same
 * UNBOUNDED `navigator.locks` wait that hangs `loadSession`. With another tab stuck holding the
 * auth lock it neither resolves nor rejects, so a `finally` after it never runs either. Losing
 * the server-side revoke is the lesser harm; leaving this browser's refresh token in
 * localStorage after telling the user they are signed out is the greater one, because the next
 * page load hydrates the session straight back.
 */
async function signOutWithinBudget(
  scope: "global" | "local"
): Promise<{ error: { message: string } | null }> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const budget = new Promise<{ error: { message: string } }>((resolve) => {
    timer = setTimeout(
      () => resolve({ error: { message: SIGN_OUT_TIMED_OUT } }),
      SIGN_OUT_TIMEOUT_MS
    );
  });
  try {
    return await Promise.race([supabase.auth.signOut({ scope }), budget]);
  } finally {
    clearTimeout(timer);
  }
}

const mapOrgRole = (row: Database["public"]["Tables"]["org_user_roles"]["Row"]): IOrgRole => {
  return {
    id: undefined,
    user_id: row.user_id as number,
    org_id: row.org_id,
    role: row.role,
    created_at: (row.created_at as string) ?? new Date().toISOString(),
    updated_at: (row.modified_at as string | null) ?? undefined,
  } as IOrgRole;
};

/**
 * Every org this profile belongs to, by all three membership paths.
 *
 * `orgRoles` drives org-level checks; `orgIds` additionally drives the onboarding "do you
 * belong to any org?" gate, which must pass for project-scoped users (subcontractors) who
 * have no `org_user_roles` row at all. Its own function because both hydration paths need
 * exactly this, and because its three error branches are what made them Codacy-complex.
 */
async function fetchOrgMembership(profileId: number): Promise<{
  error: string | null;
  orgRoles: IOrgRole[];
  orgIds: Set<string>;
}> {
  const [rolesRes, buildingOrgsRes, projectOrgsRes] = await Promise.all([
    supabase
      .from("org_user_roles")
      .select("org_id, role, user_id, created_at, modified_at")
      .eq("user_id", profileId),
    supabase.from("user_building_assignments").select("org_id").eq("user_id", profileId),
    supabase.from("user_project_assignments").select("org_id").eq("user_id", profileId),
  ]);

  const failure = [rolesRes, buildingOrgsRes, projectOrgsRes].map((res) => res.error).find(Boolean);
  if (failure) return { error: failure.message, orgRoles: [], orgIds: new Set<string>() };

  const rows = (rolesRes.data ?? []) as Database["public"]["Tables"]["org_user_roles"]["Row"][];
  const orgIdRows = [rows, buildingOrgsRes.data ?? [], projectOrgsRes.data ?? []].flat() as Array<{
    org_id: string;
  }>;

  return {
    error: null,
    orgRoles: rows.map(mapOrgRole),
    orgIds: new Set(orgIdRows.map((row) => row.org_id)),
  };
}

/**
 * AuthStore model for managing authentication state.
 * Handles authentication, session management, and organization roles.
 * User profile data is managed by UserStore.
 */
export const AuthStore = types
  .model("AuthStore", {
    userId: types.maybeNull(types.string),
    userProfileId: types.maybeNull(types.number), // Just store the ID, actual profile is in UserStore
    isPlatformAdmin: types.optional(types.boolean, false),
    orgRoles: types.array(OrgRole),
    isLoading: types.optional(types.boolean, true),
    error: types.maybeNull(types.string),
  })
  .volatile(() => ({
    permissions: new Map<string, Set<Permission>>(),
    assignedBuildings: new Set<number>(),
    assignedProjects: new Map<number, ProjectAssignment>(),
    // What the user may do in each assigned project, per the configurable
    // per-project codes (get_my_project_permissions — has_permission() itself,
    // overrides included). Loaded with, and guarded exactly like,
    // assignedProjects. `null` = the database predates per-project permissions,
    // and usePermissions answers from the role grant as it always did.
    projectPermissions: null as Map<number, Set<ConfigurablePermission>> | null,
    // Distinct org_ids the user belongs to via ANY path — org_user_roles,
    // user_building_assignments, user_project_assignments. Populated once in
    // loadSession so the onboarding gate (organizationCount) works for project-
    // scoped users (e.g. subcontractors) before any org has been selected.
    memberOrgIds: new Set<string>(),
    // Pending background re-attempts of a failed RBAC load, keyed
    // `permissions:<orgId>` / `assignments:<orgId>` — the two fail and recover
    // independently.
    rbacRetryTimers: new Map<string, ReturnType<typeof setTimeout>>(),
    // Consecutive failures per retry key, driving the backoff and the give-up
    // point in nextRbacRetryDelay. Deleted the moment a load lands.
    rbacRetryAttempts: new Map<string, number>(),
    // The org `assignedBuildings`/`assignedProjects` below describe, i.e. the
    // org most recently ASKED for. Those two caches are global rather than
    // keyed by org, so a response arriving for an org the user has already
    // left has no slot of its own to land in and would overwrite the org they
    // are on; every write checks this first. See loadAssignments.
    assignmentsOrgId: null as string | null,
    // The org `loadPermissions` was last ASKED for. Grant sets are keyed by
    // org, so a late response does have a slot of its own — but the background
    // retry does not: unguarded it keeps re-firing every 15s for an org the
    // user has left, for the rest of the session. See loadPermissions.
    permissionsOrgId: null as string | null,
    // The orgs the two loads last LANDED for, as opposed to the two above,
    // which record the org last ASKED for and are set even when that ask then
    // fails. Only these two can answer "does the store now describe org X?" —
    // a failed first load writes an empty grant set, so hasLoadedPermissions()
    // cannot tell a real empty set from a fail-closed one. See isRbacLoadedFor,
    // which the org switch watches to flush the cache once a background retry
    // finally succeeds.
    permissionsLoadedOrgId: null as string | null,
    assignmentsLoadedOrgId: null as string | null,
    // Bumped by every clearAuthState. An in-flight hydration reads it before its first
    // await and bails once it moves, because a sign-out that lands mid-flight makes every
    // row that flight is about to write stale — see loadSession.
    authEpoch: 0,
    // Background RBAC load failures live here, NOT in self.error — those loads
    // fire un-awaited (org set/switch, the 15s retry loop), so writing the
    // shared slot would clobber the error of whatever operation a form just
    // ran (SecuritySettingsForm/PersonalSettingsForm read self.error after
    // updatePassword/updateEmail).
    rbacError: null as string | null,
  }))
  .views((self) => ({
    /** True when a user is logged in */
    get isAuthenticated(): boolean {
      return self.userId !== null;
    },

    /** Set of all role names assigned across orgs */
    get allRoles(): Set<string> {
      return new Set(self.orgRoles.map((r) => r.role));
    },

    /** True if user has role across any organization */
    hasRole(role: string): boolean {
      return this.allRoles.has(role);
    },

    /** Return role for a specific organization */
    getRoleForOrg(orgId: string): IOrgRole | undefined {
      return self.orgRoles.find((r) => r.org_id === orgId);
    },

    /** Convenience admin check via permissions across any org */
    get isAdmin(): boolean {
      for (const permSet of self.permissions.values()) {
        if (permSet && (permSet as Set<any>).has("MANAGE_ORG_PERMISSIONS" as any)) return true;
      }
      return false;
    },

    /** Number of organizations the user belongs to via ANY membership path —
     *  org_user_roles OR user_building_assignments OR user_project_assignments.
     *  Backed by `memberOrgIds`, which is populated in loadSession. */
    get organizationCount(): number {
      return self.memberOrgIds.size;
    },

    // RBAC views
    getPermissions(orgId: string): Set<Permission> {
      return self.permissions.get(orgId) || new Set();
    },

    hasPermission(orgId: string, permission: Permission): boolean {
      const perms = self.permissions.get(orgId);
      return perms ? perms.has(permission) : false;
    },

    /**
     * True once loadPermissions has resolved for this org. Lets gates
     * distinguish "no permissions" from "permissions not fetched yet" —
     * on an org switch the fetch is fired without being awaited, so an
     * empty getPermissions() result alone doesn't mean access denied.
     */
    hasLoadedPermissions(orgId: string): boolean {
      return self.permissions.has(orgId);
    },

    /**
     * True once BOTH RBAC loads have SUCCEEDED for this org, i.e. the store
     * actually describes it — unlike hasLoadedPermissions(), which a
     * fail-closed empty grant set also satisfies.
     *
     * Observable, so a caller can react to a background retry finally landing:
     * the org switch invalidates the cache on a bounded wait, and anything
     * refetched while these were still empty or still the previous org's is
     * cached under a key that will not change again.
     */
    isRbacLoadedFor(orgId: string): boolean {
      return self.permissionsLoadedOrgId === orgId && self.assignmentsLoadedOrgId === orgId;
    },

    isBuildingAssigned(buildingId: number): boolean {
      return self.assignedBuildings.has(buildingId);
    },

    isProjectAssigned(projectId: number): boolean {
      return self.assignedProjects.has(projectId);
    },

    /**
     * Does the user hold the given role in this org? Supports multi-role —
     * org_user_roles is many-to-many on (user, org).
     */
    hasRoleInOrg(orgId: string, role: string): boolean {
      return self.orgRoles.some((r) => r.org_id === orgId && r.role === role);
    },
  }))
  .actions((self) => ({
    /**
     * Drop every trace of the signed-in user from this tab, synchronously.
     *
     * Its own action, and called BEFORE the sign-out request rather than after it, because
     * `supabase.auth.signOut()` awaits auth-js's `initializePromise` and then
     * `_acquireLock(-1, …)` — the same UNBOUNDED `navigator.locks` wait that hangs
     * `loadSession()`. When another tab is stuck holding the auth lock, that await neither
     * resolves nor rejects, so anything clearing state after it (a `finally` included) never
     * runs: the store would stay `isAuthenticated` with `isLoading` true, and ProtectedRoute
     * would sit on "Loading authentication…" for a user who asked to be signed out. That
     * deadlock is precisely the case AuthCallback's timeout signs the user out for.
     */
    clearAuthState() {
      // First, so that a hydration already past its own read of this abandons its writes
      // rather than restoring the user this call is dropping.
      self.authEpoch += 1;
      self.userId = null;
      self.userProfileId = null;
      self.isPlatformAdmin = false;
      self.orgRoles.replace([]);
      self.memberOrgIds = new Set<string>();
      // Volatile RBAC caches gate access (hasLoadedPermissions) — clear them
      // so a same-tab account switch can't read the previous user's grants.
      // Pending retries die with the session — a timer surviving into the
      // next login would fetch grants for an org that user never selected.
      self.rbacRetryTimers.forEach((timer) => clearTimeout(timer));
      self.rbacRetryTimers.clear();
      self.rbacRetryAttempts.clear();
      self.permissions = new Map<string, Set<Permission>>();
      self.assignedBuildings = new Set<number>();
      self.assignedProjects = new Map<number, ProjectAssignment>();
      self.projectPermissions = null;
      self.assignmentsOrgId = null;
      self.permissionsOrgId = null;
      self.permissionsLoadedOrgId = null;
      self.assignmentsLoadedOrgId = null;
      self.rbacError = null;
      // The analytics identity goes with the session, and here rather than in each caller:
      // this is the one place every sign-out routes through, so the next user of this
      // browser cannot inherit the previous one's distinct_id.
      resetUser();
      // Remembered Issues-page filters name other people and hold searches;
      // they go with the session too, by whichever path it ended.
      clearPersistedIssueFilters();
      // The react-query cache is a module-level singleton with a 5-min
      // staleTime — drop it wholesale so the next user can't be served the
      // previous user's rows as fresh (any query key, not just signing).
      queryClient.clear();
      const root = getRoot<AuthRootAccess>(self);
      // Through the stores that own the subscriptions, first: a consumer
      // holding its own channel would otherwise keep it — non-null, latch unset
      // — and a surface still mounted through a same-tab account switch would
      // believe it is subscribed to a channel that is gone, staying
      // realtime-dead until it unmounts. Each `unsubscribe` drops the reference
      // as well as the channel, so the next subscribe starts clean.
      // Guarded ONE CALL AT A TIME rather than as a single block: every call
      // below is best-effort cleanup, and a throw in any of them (an MST action
      // on an already-destroyed node, a store shape that moved) must skip
      // neither the teardowns after it — which would leave the previous user's
      // channels joined, exactly what this block exists to prevent — nor the
      // `isLoading = false` that stops ProtectedRoute hanging on "Loading
      // authentication…", the very hang this action exists to prevent.
      const safe = (what: string, call: () => void) => {
        try {
          call();
        } catch (e) {
          // `what` stays an argument: the format string is a literal, which is
          // what the static analysis asks for.
          console.error("[auth] realtime teardown failed on logout", what, e);
        }
      };
      safe("documentStore", () => root.documentStore?.unsubscribe());
      safe("chatStore.queues", () => root.chatStore?.clearSessionQueues());
      safe("organizationStore", () => root.organizationStore?.unsubscribeFromOrgChanges());
      safe("messagesStore", () => root.messagesStore?.unsubscribe());
      safe("unreadContext", () => root.messagesStore?.clearUnreadContext());
      safe("pmStore.issues", () => root.pmStore?.issues?.unsubscribe());
      safe("buildingStore", () => root.buildingStore?.unsubscribeAllBuildingData());
      // The same bookkeeping for channels opened through the shared primitive
      // (`useRealtimeInvalidate` and anything else holding a handle): their
      // teardown latch has to be set, or a late status calls back into a
      // consumer whose session is gone.
      safe("resyncChannels", () => teardownAllResyncChannels());
      // supabase-js clears the realtime token on SIGNED_OUT but never leaves
      // the channels (SupabaseClient `_handleTokenChanged`), so a same-tab
      // account switch would carry the previous user's joins into the next
      // session and keep feeding their rows into the new user's stores. Still
      // swept app-wide after the owners above, for the channels held in
      // component effects (chat, invitations, chatbot attachments): those are
      // not reachable from here, and every one of them sits under
      // ProtectedRoute, which renders <Navigate to="/login"> the moment
      // `isAuthenticated` goes false — so they unmount, run their own
      // cleanup, and subscribe afresh on the next login.
      safe("removeAllChannels", () => {
        supabase
          .removeAllChannels()
          // Reported from the RESOLVED value: each leave resolves with
          // 'ok' | 'timed out' | 'error' rather than rejecting, so a channel
          // the previous session failed to leave is silent unless read here.
          .then((statuses) => {
            const failed = statuses.filter((status) => status !== "ok");
            if (failed.length > 0) {
              console.error("[auth] realtime channels did not leave cleanly", failed);
            }
          })
          // Kept for the throw a leave can still make (the socket's own state):
          // `void` does not handle a rejection, and one during logout would
          // surface as an unhandled rejection.
          .catch((e) => {
            console.error("[auth] failed to leave realtime channels on logout", e);
          });
      });
      self.isLoading = false;

      if (root.userStore) {
        root.userStore.clearProfile();
      }
    },
  }))
  .actions((self) => ({
    setOrgRoles(roles: IOrgRole[]) {
      self.orgRoles.replace(roles);
    },

    /**
     * Hydrate the store from the current Supabase session.
     *
     * Resolves to the message of the failure that stopped hydration, or null when the
     * store ended up consistent — either fully hydrated, or signed out with `userId` null
     * too. It never resolves to null half-hydrated, so a caller that navigates on a null
     * can rely on the roles and memberships being there. A sign-out that lands mid-flight
     * is reported the same way, so a caller cannot navigate a just-signed-out user onward.
     * A caller
     * that awaits this MUST read that value rather than `self.error`: AuthProvider fires
     * its own un-awaited loadSession() at mount, so two flows write the shared slot on
     * the same page load and whichever finishes last wins — the same clobbering that
     * `rbacError` exists to avoid.
     */
    loadSession: flow(function* loadSession() {
      // Read BEFORE the first await, and re-checked after every one: a sign-out that lands
      // while this is in flight (AuthCallback's timeout or failAndSignOut, both firing at a
      // parallel AuthProvider hydration) has already cleared the store, and writing the
      // session captured before it would sign the user straight back in — half-hydrated,
      // `isAuthenticated` with no orgs, which is what bounces an existing member to
      // /create-organization. Bail instead, leaving the signed-out state alone. An epoch
      // rather than a userId comparison, because a logout leaves userId null, which is also
      // what this reads on a first, uncontested load.
      const epoch = self.authEpoch;
      const supersededBySignOut = () => self.authEpoch !== epoch;

      self.isLoading = true;
      self.error = null;
      try {
        const { data, error } = yield supabase.auth.getSession();
        if (supersededBySignOut()) return SIGNED_OUT_MID_LOAD;
        if (error) {
          self.error = error.message;
          return error.message;
        }
        const session = data?.session;
        if (!session?.user) {
          // The whole identity goes, not just the ids: `orgRoles`, `memberOrgIds` and
          // `isPlatformAdmin` left behind would be the half-hydrated null this function's
          // contract rules out — the previous user's roles under a null `userId`.
          // Guarded on there being an identity at all, because clearAuthState bumps
          // `authEpoch` (and drops the query cache): on an ordinary anonymous page load
          // there is nothing to clear, and bumping would make a hydration running in
          // parallel bail as though a sign-out had landed.
          if (self.userId || self.userProfileId) self.clearAuthState();
          return null;
        }
        self.userId = session.user.id;

        // Just get the profile ID and platform admin flag - actual profile data is loaded by UserStore
        const { data: profileRow, error: profileError } = yield fetchProfileRow(session.user.id);
        if (supersededBySignOut()) return SIGNED_OUT_MID_LOAD;

        if (profileError) {
          self.error = profileError.message;
          return profileError.message;
        }

        // A signed-in user with no readable profile row is a FAILURE, not an empty store:
        // every membership below is keyed on `user_profiles.id`, so hydration stops here with
        // `userId` set, no roles and no orgs. Reporting that as success would let AuthCallback
        // navigate on, and ProtectedRoute would forward an existing member to
        // /create-organization — the bounce that page exists to prevent. The row is created by
        // the `handle_new_user` trigger, so missing means the trigger failed or the row is not
        // yet visible to the just-issued JWT, which is exactly the brand-new OAuth user's path
        // — fetchProfileRow has already re-read it once to give that race time to close.
        if (!profileRow) {
          self.userProfileId = null;
          self.isPlatformAdmin = false;
          self.orgRoles.replace([]);
          self.memberOrgIds = new Set<string>();
          self.error = NO_PROFILE_ROW_ERROR;
          return NO_PROFILE_ROW_ERROR;
        }

        self.userProfileId = profileRow.id;
        self.isPlatformAdmin = profileRow.is_platform_admin ?? false;

        const membership = yield fetchOrgMembership(profileRow.id);
        if (supersededBySignOut()) return SIGNED_OUT_MID_LOAD;
        if (membership.error) {
          self.error = membership.error;
          return membership.error;
        }
        self.orgRoles.replace(membership.orgRoles);
        self.memberOrgIds = membership.orgIds;

        // After successful auth load, trigger UserStore to load profile
        const root = getRoot<AuthRootAccess>(self);
        if (root.userStore) {
          yield root.userStore.loadProfile(session.user.id);
          // UserStore has no such guard of its own, so drop what its read just put back
          // into the store the sign-out cleared.
          if (supersededBySignOut()) {
            root.userStore.clearProfile();
            return SIGNED_OUT_MID_LOAD;
          }
        }
        return null;
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : String(e);
        self.error = message;
        return message;
      } finally {
        self.isLoading = false;
      }
    }),

    // RBAC: load permissions for an org.
    //
    // Resolves to whether a fresh grant set landed — never rejects, because
    // every caller fires it un-awaited and it retries itself. A caller that
    // awaits it (the org switch) MUST read that boolean: a `.catch()` on this
    // promise is dead code.
    loadPermissions: flow(function* loadPermissions(orgId: string) {
      const profileId = self.userProfileId;
      if (!profileId) return false;
      const retryKey = `permissions:${orgId}`;
      // A load for a different org than the one last ASKED for is a switch (or
      // a first selection), never the background retry — that only re-fires
      // while this org is still the one asked for. Both markers therefore start
      // clean:
      //  - the LANDED marker, because it still names the org whose grants
      //    landed earlier, and leaving it would let isRbacLoadedFor() answer
      //    true for an org whose current load then fails (A ok → B → A failing),
      //    firing the switch's late-RBAC flush immediately instead of when the
      //    org's RBAC actually lands;
      //  - the retry budget, so coming back to an org whose attempts were spent
      //    gets the fresh start the give-up line promises rather than failing
      //    closed for the rest of the session.
      if (self.permissionsOrgId !== orgId) {
        self.permissionsLoadedOrgId = null;
        self.rbacRetryAttempts.delete(retryKey);
      }
      self.permissionsOrgId = orgId;
      // One immediate retry before failing closed — a single transient
      // transport error must not wipe a valid grant set for the whole org.
      // ponytail: no backoff; add one if flaky networks still trip this.
      let permSet: Set<Permission> | null = null;
      let lastError: unknown = null;
      for (let attempt = 0; attempt < 2 && permSet === null; attempt++) {
        try {
          permSet = yield permRepo.fetchUserPermissions(profileId, orgId);
        } catch (e: unknown) {
          lastError = e;
        }
      }
      // Callers fire this un-awaited (org set/switch). If the user logged out,
      // switched accounts or switched orgs mid-flight, writing now would
      // repopulate the cache logout just cleared with the previous user's
      // grants, or clear an rbacError the org they are actually on just set.
      if (self.userProfileId !== profileId || self.permissionsOrgId !== orgId) return false;
      clearTimeout(self.rbacRetryTimers.get(retryKey));
      self.rbacRetryTimers.delete(retryKey);
      if (permSet !== null) {
        self.rbacRetryAttempts.delete(retryKey);
        self.permissionsLoadedOrgId = orgId;
        // Replace the Map instead of mutating it — volatile props are only
        // observable by reference, so an in-place .set() would not notify
        // observers waiting on hasLoadedPermissions()/getPermissions().
        self.permissions = new Map(self.permissions).set(orgId, permSet);
        self.rbacError = null;
        return true;
      }
      self.rbacError = errorMessage(lastError);
      // Fail closed only when this org has never loaded — an empty entry
      // lets gates keyed on hasLoadedPermissions() deny instead of waiting
      // forever. A previously loaded grant set survives a failed re-fetch
      // (stale beats bricked app-wide); the retry below refreshes it.
      if (!self.permissions.has(orgId)) {
        self.permissions = new Map(self.permissions).set(orgId, new Set());
      }
      scheduleRbacRetry(self, { retryKey, what: "permissions", orgId, error: lastError }, () => {
        // Dropped once the user has left this org, like loadAssignments: a
        // permanently failing org would otherwise keep a request pair in
        // flight and keep writing grants for an org nobody is on.
        if (self.permissionsOrgId !== orgId) return;
        void (
          self as unknown as { loadPermissions(orgId: string): Promise<boolean> }
        ).loadPermissions(orgId);
      });
      return false;
    }),

    // RBAC: load building/project assignments for an org.
    //
    // Resolves to whether `assignedBuildings`/`assignedProjects` now describe
    // `orgId`; never rejects, same contract as loadPermissions.
    //
    // Unlike permissions those two caches are GLOBAL, not keyed by org, so a
    // response has no slot of its own: on a rapid A→B→C switch, B's in-flight
    // fetch would land on top of C's. Every write therefore checks
    // `assignmentsOrgId` — the org last asked for — and a superseded response
    // is dropped.
    loadAssignments: flow(function* loadAssignments(orgId: string) {
      const profileId = self.userProfileId;
      if (!profileId) return false;
      const retryKey = `assignments:${orgId}`;
      // On a SWITCH these two still hold the org being left, and building and
      // project ids are unique across orgs — so every gate read during the
      // in-flight window would answer out of the previous org's scope. Empty
      // fails closed instead, which is what the catch below settles on anyway.
      // A re-load of the SAME org (the background retry, a second selection)
      // keeps what it has: wiping there would deny a subcontractor everything
      // on each retry tick.
      const switchedOrg = self.assignmentsOrgId !== orgId;
      if (switchedOrg) {
        self.assignedBuildings = new Set<number>();
        self.assignedProjects = new Map<number, ProjectAssignment>();
        // Empty, not null: null means "answer from the role", which on a
        // switch would answer out of whatever the role grants org-wide.
        self.projectPermissions = new Map<number, Set<ConfigurablePermission>>();
        // Those two no longer describe whatever org last landed, so the marker
        // must not keep claiming they do — otherwise an A→B→A→B walk where B's
        // second load fails reads as "B is loaded" over empty caches.
        self.assignmentsLoadedOrgId = null;
        // A switch is also the fresh start the give-up line promises: without
        // this, an org whose 5 attempts were spent earlier in the session gets
        // no background retry ever again, so one failure on returning to it
        // denies project-scoped access until a reload.
        self.rbacRetryAttempts.delete(retryKey);
      }
      self.assignmentsOrgId = orgId;
      try {
        const buildings: Set<number> = yield permRepo.fetchBuildingAssignments(profileId, orgId);
        const projects: ProjectAssignment[] = yield permRepo.fetchProjectAssignments(
          profileId,
          orgId
        );
        const projectPermissions: Map<number, Set<ConfigurablePermission>> | null =
          yield permRepo.fetchMyProjectPermissions(orgId);
        // Same un-awaited-caller guard as loadPermissions, plus the org one.
        if (self.userProfileId !== profileId || self.assignmentsOrgId !== orgId) return false;
        clearTimeout(self.rbacRetryTimers.get(retryKey));
        self.rbacRetryTimers.delete(retryKey);
        self.rbacRetryAttempts.delete(retryKey);
        self.assignedBuildings = buildings;
        self.assignedProjects = new Map(projects.map((p) => [p.projectId, p]));
        self.projectPermissions = projectPermissions;
        self.assignmentsLoadedOrgId = orgId;
        return true;
      } catch (e: unknown) {
        if (self.userProfileId !== profileId || self.assignmentsOrgId !== orgId) return false;
        self.rbacError = errorMessage(e);
        // Fail closed ONLY on a switch, where whatever these two hold belongs
        // to the org just left — building and project ids are unique across
        // orgs, so keeping them would grant the new org's session affordances
        // derived from the PREVIOUS org's assignments, and a switch that failed
        // here would silently carry access across the boundary it exists to
        // draw. (The entry guard above has already emptied them in that case;
        // this is the same fail-closed decision restated for the catch.)
        //
        // A re-load of the SAME org keeps what it has, exactly as the entry
        // guard decided: those assignments describe this org and are merely
        // stale. Wiping on a transient failure here would deny a subcontractor
        // every project-scoped affordance until a retry lands, which is the
        // outcome the entry guard exists to avoid.
        if (switchedOrg) {
          self.assignedBuildings = new Set<number>();
          self.assignedProjects = new Map<number, ProjectAssignment>();
          self.projectPermissions = new Map<number, Set<ConfigurablePermission>>();
          self.assignmentsLoadedOrgId = null;
        }
        // Loudly, because the failure mode is indistinguishable from the
        // legitimate one: assignedProjects stays EMPTY, and an empty map reads
        // exactly like "this user is assigned to nothing". Every PROJECT_SCOPED
        // permission then answers false, so a subcontractor silently loses
        // Rename, project writes and the rest, while owners and admins notice
        // nothing (hasPermission short-circuits for them). A PGRST201 embed
        // ambiguity did precisely this on develop and presented as a
        // permissions bug; the console line is what turns the next one into a
        // five-minute diagnosis.
        // The org id is an ARGUMENT, not interpolated into the first string:
        // that argument is console's format string, so a `%s` arriving inside
        // orgId would swallow the ones after it and forge the line.
        console.error(
          "[rbac] project/building assignments failed to load — project-scoped " +
            "permissions will be denied until this succeeds. org:",
          orgId,
          e
        );
        scheduleRbacRetry(self, { retryKey, what: "assignments", orgId, error: e }, () => {
          // Dropped if the user has moved on: the org this would write is no
          // longer the org those caches are meant to describe.
          if (self.assignmentsOrgId !== orgId) return;
          void (
            self as unknown as { loadAssignments(orgId: string): Promise<boolean> }
          ).loadAssignments(orgId);
        });
        return false;
      }
    }),

    loginWithEmailPassword: flow(function* loginWithEmailPassword(email: string, password: string) {
      self.isLoading = true;
      self.error = null;
      try {
        const { data, error } = yield supabase.auth.signInWithPassword({ email, password });
        if (error) {
          self.error = error.message;
          return;
        }
        const user = data?.user;
        if (!user) {
          self.error = "No user returned from Supabase.";
          return;
        }
        self.userId = user.id;
        // Identity as soon as the credentials check out — a session exists from here, so this
        // browser is that person's whatever the loads below do, and every event of this login
        // is attributed to them rather than to the anonymous id it has been carrying.
        // PostHogIdentity repeats the identify once the profile id is known. The EVENT waits:
        // see the end of this flow.
        identifyUser(user.id);

        // Just get the profile ID and platform admin flag - actual profile data is loaded by UserStore
        const { data: profileRow, error: profileError } = yield supabase
          .from("user_profiles")
          .select("id, is_platform_admin")
          .eq("auth_user_id", user.id)
          .single();

        if (profileError) {
          self.error = profileError.message;
          return;
        }

        self.userProfileId = profileRow.id;
        self.isPlatformAdmin = profileRow.is_platform_admin ?? false;

        const membership = yield fetchOrgMembership(profileRow.id);
        if (membership.error) {
          self.error = membership.error;
          return;
        }
        self.orgRoles.replace(membership.orgRoles);
        self.memberOrgIds = membership.orgIds;

        // Only now: the credentials passing is not a sign-in. Both reads above return early
        // with `self.error` set and the user left on the login screen, so counting the event
        // at the password check would put every one of those failures in the funnel as a
        // success — overstating the sign-in rate exactly on the failure mode worth seeing.
        // The profile load below is not part of that test: it keeps its own error and the
        // user is signed in either way.
        track("signed_in", { method: "password" });

        // After successful login, trigger UserStore to load profile
        const root = getRoot<AuthRootAccess>(self);
        if (root.userStore) {
          yield root.userStore.loadProfile(user.id);
        }
      } catch (e: unknown) {
        self.error = e instanceof Error ? e.message : String(e);
      } finally {
        self.isLoading = false;
      }
    }),

    // `scope` is auth-js's: "global" (the default) revokes the refresh tokens of every
    // session this user has, on every device; "local" ends only this browser's. A
    // deliberate logout means all of them, but cleanup after a failed sign-in must not
    // reach past this tab — see AuthCallback's failAndSignOut.
    logout: flow(function* logout(scope: "global" | "local" = "global") {
      self.error = null;
      // Before the request, never after it, and never in a `finally` — see clearAuthState.
      // A hung signOut then costs the user only the server-side revoke, not this tab.
      self.clearAuthState();
      try {
        const { error } = yield signOutWithinBudget(scope);
        if (error) {
          self.error = error.message;
        }
      } catch (e: unknown) {
        self.error = e instanceof Error ? e.message : String(e);
      } finally {
        // However that ended — returned, failed, threw, or ran out its budget on the auth
        // lock — this browser's token goes. It is deferred to here, rather than run before
        // the request, only so a `scope: "global"` revoke still has a session to send: wiping
        // first would silently downgrade "sign me out everywhere" to this browser only. What
        // it must never do is be skipped, which is what leaving it inside the `try` did — a
        // signOut hung on the lock left `sb-<ref>-auth-token` in localStorage, and the next
        // page load hydrated the very session the user had been told was over.
        clearSupabaseAuthStorage();
      }
    }),

    updatePassword: flow(function* updatePassword(password: string) {
      self.error = null;
      try {
        const { error } = yield supabase.auth.updateUser({ password });
        if (error) {
          self.error = error.message;
          return false;
        }
        return true;
      } catch (e: unknown) {
        self.error = e instanceof Error ? e.message : String(e);
        return false;
      }
    }),

    updateEmail: flow(function* updateEmail(email: string) {
      self.error = null;
      try {
        const { error } = yield supabase.auth.updateUser({ email });
        if (error) {
          self.error = error.message;
          return false;
        }
        return true;
      } catch (e: unknown) {
        self.error = e instanceof Error ? e.message : String(e);
        return false;
      }
    }),
  }));

export type AuthStoreInstance = Instance<typeof AuthStore>;
export type AuthStoreSnapshotIn = SnapshotIn<typeof AuthStore>;
export type AuthStoreSnapshotOut = SnapshotOut<typeof AuthStore>;
